/**
 * mbDataLayer: eventos GA4 de comercio electrónico en window.dataLayer (para el contenedor GTM).
 *
 * API pública: mbDataLayer.push(evento, datos)
 *   - Si `datos.items` es una lista, se empuja { event, ecommerce: { currency, ...datos } } (y antes { ecommerce: null }).
 *   - Si no, se empuja { event, ...datos } tal cual (search, generate_lead, rescate_*...).
 *
 * Eventos: view_item, view_item_list, select_item, add_to_cart, remove_from_cart, view_cart, begin_checkout,
 * search, generate_lead, newsletter_signup, rescate_visto / rescate_aceptado / rescate_cerrado.
 *
 * Cómo se enganchan los componentes: add_to_cart / remove_from_cart se obtienen observando las respuestas de
 * /cart/add, /cart/change, /cart/update y /cart/clear (fetch), así cubren a la vez la ficha granel, el tarro,
 * la tarjeta de compra rápida (mb-tarjeta.js), los complementos del carrito (mb-carrito-complementos.js),
 * el regalo, el rescate y los componentes de Horizon (cajón y página de carrito), sin tocar cada fichero.
 *
 * Consentimiento: se empuja SIEMPRE (como hacía Millstore, que no condicionaba nada). Quien decide si una etiqueta
 * se dispara es GTM (modo de consentimiento / disparadores del contenedor).
 *
 * Los datos de la página salen de <script id="mb-dl-datos"> (snippets/mb-datalayer.liquid).
 */
(() => {
  if (window.mbDataLayer) return;
  const w = window;
  w.dataLayer = w.dataLayer || [];

  /** @type {any} */
  let datos = {};
  const leerDatos = () => {
    try {
      datos = JSON.parse(document.getElementById('mb-dl-datos')?.textContent || '{}');
    } catch {
      datos = {};
    }
  };
  leerDatos();

  const redondear = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const valorDe = (items) => redondear(items.reduce((t, i) => t + (Number(i.price) || 0) * (Number(i.quantity) || 1), 0));

  /**
   * @param {string} evento
   * @param {Record<string, any>} [carga]
   */
  function push(evento, carga = {}) {
    const paquete = { event: evento };
    if (Array.isArray(carga?.items)) {
      w.dataLayer.push({ ecommerce: null });
      paquete.ecommerce = { currency: datos.moneda || 'EUR', ...carga };
    } else {
      Object.assign(paquete, carga);
    }
    w.dataLayer.push(paquete);
  }

  /** Línea de /cart.js o /cart/add.js a artículo GA4. */
  const deLinea = (l, cantidad) => ({
    item_id: `shopify_ES_${l.product_id}_${l.variant_id ?? l.id}`,
    item_name: l.product_title ?? l.title,
    item_brand: l.vendor ?? '',
    item_category: l.product_type ?? '',
    item_variant: l.variant_title && l.variant_title !== 'Default Title' ? l.variant_title : '',
    price: redondear((l.final_price ?? l.price) / 100),
    quantity: cantidad ?? l.quantity,
    product_id: String(l.product_id),
    variant_id: String(l.variant_id ?? l.id),
    sku: l.sku ?? '',
  });

  /* ---------- Instantánea del carrito (para calcular qué se ha quitado) ---------- */
  /** @type {any} */
  let carrito = null;
  const fetchOriginal = w.fetch.bind(w);
  const raiz = () => w.Shopify?.routes?.root ?? '/';

  const leerCarrito = () =>
    fetchOriginal(`${raiz()}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (j?.items) carrito = j;
        return carrito;
      })
      .catch(() => carrito);

  /** Líneas nuevas en la instantánea tras un add.js. */
  function fusionarAnadidos(anadidos) {
    if (!carrito) return;
    for (const a of anadidos) {
      const existente = carrito.items.find((i) => i.key === a.key);
      if (existente) existente.quantity += a.quantity;
      else carrito.items.push({ ...a });
    }
  }

  /** @param {any} cuerpo @returns {Record<string, any>} */
  function leerCuerpo(cuerpo) {
    try {
      if (!cuerpo) return {};
      if (typeof cuerpo === 'string') {
        try {
          return JSON.parse(cuerpo);
        } catch {
          return Object.fromEntries(new URLSearchParams(cuerpo));
        }
      }
      if (cuerpo instanceof FormData || cuerpo instanceof URLSearchParams) return Object.fromEntries(cuerpo.entries());
    } catch {
      /* la medición nunca debe romper la compra */
    }
    return {};
  }

  /** Línea previa a la que apunta una petición de change/update. */
  function lineaPrevia(previo, clave) {
    if (!previo) return null;
    const texto = String(clave);
    return (
      previo.items.find((i) => i.key === texto) ||
      previo.items.find((i) => String(i.variant_id) === texto) ||
      null
    );
  }

  /** Empuja add_to_cart / remove_from_cart a partir de diferencias de cantidad. */
  function emitirDiferencias(cambios) {
    const anadidos = cambios.filter((c) => c.delta > 0).map((c) => deLinea(c.linea, c.delta));
    const quitados = cambios.filter((c) => c.delta < 0).map((c) => deLinea(c.linea, -c.delta));
    if (anadidos.length) push('add_to_cart', { value: valorDe(anadidos), items: anadidos });
    if (quitados.length) push('remove_from_cart', { value: valorDe(quitados), items: quitados });
  }

  function procesar(accion, cuerpo, previo, json) {
    if (accion === 'add') {
      const lineas = (Array.isArray(json?.items) ? json.items : [json]).filter((l) => l && l.product_id);
      if (!lineas.length) return;
      const items = lineas.map((l) => deLinea(l));
      push('add_to_cart', { value: valorDe(items), items });
      fusionarAnadidos(lineas);
      return;
    }

    if (accion === 'change' && previo) {
      const clave = cuerpo.id ?? (cuerpo.line ? previo.items[Number(cuerpo.line) - 1]?.key : undefined);
      const linea = clave ? lineaPrevia(previo, clave) : null;
      if (linea) emitirDiferencias([{ linea, delta: Number(cuerpo.quantity) - linea.quantity }]);
    } else if (accion === 'update' && previo && cuerpo.updates) {
      const cambios = [];
      const u = cuerpo.updates;
      const pares = Array.isArray(u) ? u.map((q, i) => [previo.items[i]?.key, q]) : Object.entries(u);
      for (const [clave, cantidad] of pares) {
        const linea = clave ? lineaPrevia(previo, clave) : null;
        if (linea) cambios.push({ linea, delta: Number(cantidad) - linea.quantity });
      }
      emitirDiferencias(cambios);
    } else if (accion === 'clear' && previo) {
      emitirDiferencias(previo.items.map((linea) => ({ linea, delta: -linea.quantity })));
    }

    if (json?.items) carrito = json;
  }

  /** @type {Promise<void>} */
  let cola = Promise.resolve();
  const RUTA_ACCION = /\/cart\/(add|change|update|clear)(?:\.js)?$/;
  const RUTA_LECTURA = /\/cart(?:\.js|\.json)$/;

  w.fetch = function mbFetch(entrada, opciones) {
    let ruta = '';
    try {
      const url = typeof entrada === 'string' ? entrada : entrada instanceof URL ? entrada.href : entrada?.url;
      ruta = url ? new URL(url, location.href).pathname : '';
    } catch {
      ruta = '';
    }
    const coincidencia = RUTA_ACCION.exec(ruta);
    const lectura = !coincidencia && RUTA_LECTURA.test(ruta);
    if (!coincidencia && !lectura) return fetchOriginal(entrada, opciones);

    const llamar = () => {
      const promesa = fetchOriginal(entrada, opciones);
      const cuerpo = coincidencia ? leerCuerpo(opciones?.body) : {};
      // Las respuestas se procesan en el orden de las peticiones, así `carrito` es siempre el estado previo.
      cola = cola
        .then(() => promesa)
        .then((respuesta) => (respuesta.ok ? respuesta.clone().json() : null))
        .then((json) => {
          if (!json) return;
          if (lectura) {
            if (json.items) carrito = json;
          } else {
            procesar(coincidencia[1], cuerpo, carrito, json);
          }
        })
        .catch(() => {});
      return promesa;
    };

    if (coincidencia && coincidencia[1] !== 'add' && !carrito) {
      carrito = w.mbCarrito?.items ? w.mbCarrito : null;
      if (!carrito) return leerCarrito().then(llamar);
    }
    return llamar();
  };

  /* ---------- Listas de productos: view_item_list y select_item ---------- */
  /** @param {Element} tarjeta */
  function itemDeTarjeta(tarjeta) {
    try {
      return JSON.parse(/** @type {HTMLElement} */ (tarjeta).dataset.mbItem || 'null');
    } catch {
      return null;
    }
  }

  /** @param {Element} tarjeta */
  function listaDeTarjeta(tarjeta) {
    if (datos.lista && tarjeta.closest('[data-mb-grid]')) return { id: datos.lista.id, nombre: datos.lista.nombre };
    const seccion = tarjeta.closest('section, .shopify-section');
    const titulo = seccion?.querySelector('h2')?.textContent?.replace(/\s+/g, ' ').trim().slice(0, 60);
    const nombre = titulo || datos.plantilla || 'lista';
    const id = nombre
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
    return { id, nombre };
  }

  /** Posición (1..n) de la tarjeta dentro de su lista. */
  function posicion(tarjeta) {
    const grupo = tarjeta.closest('ul, ol, section, .shopify-section') || document;
    return [...grupo.querySelectorAll('[data-mb-item]')].indexOf(tarjeta) + 1;
  }

  /** @type {Map<string, any[]>} */
  const pendientes = new Map();
  /** @type {number | undefined} */
  let temporizador;
  const vistas = new WeakSet();

  function volcarListas() {
    for (const [clave, items] of pendientes) {
      const [id, nombre] = clave.split('\u0000');
      push('view_item_list', { item_list_id: id, item_list_name: nombre, value: valorDe(items), items });
    }
    pendientes.clear();
  }

  const observador =
    'IntersectionObserver' in w
      ? new IntersectionObserver(
          (entradas) => {
            for (const entrada of entradas) {
              if (!entrada.isIntersecting) continue;
              const tarjeta = entrada.target;
              observador?.unobserve(tarjeta);
              const item = itemDeTarjeta(tarjeta);
              if (!item) continue;
              const lista = listaDeTarjeta(tarjeta);
              const clave = `${lista.id}\u0000${lista.nombre}`;
              const items = pendientes.get(clave) ?? [];
              items.push({ ...item, index: posicion(tarjeta), item_list_id: lista.id, item_list_name: lista.nombre });
              pendientes.set(clave, items.slice(0, 50));
            }
            if (pendientes.size) {
              clearTimeout(temporizador);
              temporizador = setTimeout(volcarListas, 500);
            }
          },
          { threshold: 0.5 }
        )
      : null;

  /** @param {ParentNode} raiz */
  function observarTarjetas(raiz) {
    for (const tarjeta of raiz.querySelectorAll('[data-mb-item]')) {
      if (vistas.has(tarjeta)) continue;
      vistas.add(tarjeta);
      observador?.observe(tarjeta);
    }
  }

  document.addEventListener(
    'click',
    (evento) => {
      const objetivo = /** @type {Element} */ (evento.target);
      const tarjeta = objetivo?.closest?.('[data-mb-item]');
      if (tarjeta && objetivo.closest('a')) {
        const item = itemDeTarjeta(tarjeta);
        if (item) {
          const lista = listaDeTarjeta(tarjeta);
          push('select_item', {
            item_list_id: lista.id,
            item_list_name: lista.nombre,
            items: [{ ...item, index: posicion(tarjeta), item_list_id: lista.id, item_list_name: lista.nombre }],
          });
        }
      }

      if (objetivo?.closest?.('[name="checkout"], a[href*="/checkout"]')) iniciarPago();
    },
    true
  );

  /* ---------- Carrito ---------- */
  let ultimoPago = 0;
  function iniciarPago() {
    if (Date.now() - ultimoPago < 1500) return;
    ultimoPago = Date.now();
    const items = carrito?.items?.map((l) => deLinea(l)) ?? datos.carrito?.items ?? [];
    push('begin_checkout', { value: valorDe(items), items });
  }

  async function verCarrito() {
    const actual = await leerCarrito();
    const items = actual?.items?.map((l) => deLinea(l)) ?? datos.carrito?.items ?? [];
    push('view_cart', { value: valorDe(items), items });
  }

  document.addEventListener('theme-drawer:open', (evento) => {
    if (/** @type {Element} */ (evento.target)?.id === 'cart-drawer') verCarrito();
  });

  /* ---------- Rescate ---------- */
  w.addEventListener('mb:rescate', (evento) => {
    const { accion, tipo } = /** @type {CustomEvent} */ (evento).detail ?? {};
    if (accion) push(`rescate_${accion}`, { rescate_tipo: tipo ?? '' });
  });

  /* ---------- Carga de página ---------- */
  function alCargar() {
    leerDatos();

    if (datos.producto) push('view_item', { value: datos.producto.price, items: [datos.producto] });

    if (datos.plantilla === 'cart') verCarrito();

    if (datos.busqueda?.termino) {
      push('search', { search_term: datos.busqueda.termino, results_count: datos.busqueda.resultados });
    }

    for (const marca of document.querySelectorAll('[data-mb-lead]')) {
      const tipo = /** @type {HTMLElement} */ (marca).dataset.mbLead || 'contacto';
      if (tipo === 'newsletter') push('newsletter_signup', { form: tipo });
      else push('generate_lead', { lead_type: tipo, form_id: tipo, value: 0, currency: datos.moneda || 'EUR' });
    }

    observarTarjetas(document);
    // Rejillas que cambian (filtros, "ver más", pestañas): se observan las tarjetas nuevas.
    new MutationObserver((cambios) => {
      for (const cambio of cambios) {
        for (const nodo of cambio.addedNodes) {
          if (nodo instanceof Element) observarTarjetas(nodo.matches('[data-mb-item]') ? nodo.parentElement ?? document : nodo);
        }
      }
    }).observe(document.querySelector('main') ?? document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', alCargar, { once: true });
  else alCargar();

  w.mbDataLayer = { push, version: 1 };
})();
